import { useState, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Shield, Edit3, Save, X, RotateCcw, Info } from "lucide-react";
import { useMutation as useConvexMutation, useQuery as useConvexQuery } from "convex/react";
import { api } from "../../api";
import { toast } from "sonner";
import { useRestrictionHierarchy } from "@/hooks/useRestrictionHierarchy";
import { ErrorBoundary } from "./ErrorBoundary";
import { type GenericId as Id } from "convex/values";
interface Device {
  id: string;
  name: string;
  user: string;
  os: string;
  version: string;
  status: "active" | "prepared" | "inactive";
  lastSync: string;
  group: string;
}
interface DeviceRestrictionsContentProps {
  device: Device;
  activeSection: string;
}
export const DeviceRestrictionsContent = ({
  device
}: DeviceRestrictionsContentProps) => {
  console.log('DeviceRestrictionsContent initializing for device:', device.id);
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, boolean>>({});
  
  const {
    flatRestrictions,
    shouldChildBeDisabled,
    shouldChildBeHidden,
    restrictions
  } = useRestrictionHierarchy();

  // Get device details first to get deviceConfig
  const deviceDetails = useConvexQuery(api.devices.get.details, {
    device: device.id as Id<"devices">
  });
  
  console.log('Device details query result:', {
    deviceId: device.id,
    deviceDetails: deviceDetails,
    hasDeviceConfig: !!deviceDetails?.device?.deviceConfig
  });

  // Get device restrictions using the deviceConfig from details
  const deviceRestrictionsQuery = useConvexQuery(api.devices.restrictions.get, deviceDetails?.device?.deviceConfig ? {
    deviceConfig: deviceDetails.device.deviceConfig
  } : "skip");
  const deviceRestrictions = deviceRestrictionsQuery || {};
  const isLoadingRestrictions = deviceRestrictionsQuery === undefined || deviceDetails === undefined;

  // Update device restrictions mutation
  const updateDeviceRestrictions = useConvexMutation(api.devices.restrictions.update);
  const [isSaving, setIsSaving] = useState(false);
  const getCurrentRestrictionValues = (): Record<string, boolean> => {
    const values: Record<string, boolean> = {};
    restrictions.forEach(setting => {
      if (isEditMode && pendingChanges.hasOwnProperty(setting.type)) {
        values[setting.type] = pendingChanges[setting.type];
      } else {
        // Check device restrictions from API response
        const restrictionValue = deviceRestrictions[setting.backend_name];
        if (restrictionValue === "device" || restrictionValue === "level") {
          values[setting.type] = false; // Restricted
        } else {
          // Missing from object or false = Allowed
          values[setting.type] = true; // Allowed
        }
      }
    });
    return values;
  };
  
  const getRestrictionValue = (restrictionType: string): boolean => {
    const currentValues = getCurrentRestrictionValues();
    return currentValues[restrictionType] ?? true;
  };
  const getRestrictionSource = (restrictionType: string): {
    source: string;
    levelName?: string;
  } => {
    const restriction = restrictions.find(r => r.type === restrictionType);
    if (!restriction) return {
      source: 'default'
    };
    const restrictionValue = deviceRestrictions[restriction.backend_name];
    if (restrictionValue === "device") {
      return {
        source: 'device'
      };
    } else if (restrictionValue === "level") {
      return {
        source: 'level'
      };
    } else if (restrictionValue === false) {
      return {
        source: 'device'
      };
    } else {
      // Missing from object = Default
      return {
        source: 'default'
      };
    }
  };
  const getSourceDisplay = (source: string, levelName?: string) => {
    switch (source) {
      case 'level':
        return 'Level';
      case 'device':
        return 'Device';
      default:
        return 'Default';
    }
  };
  const handleEditRestriction = (restrictionType: string, value: boolean) => {
    console.log('=== handleEditRestriction called ===', {
      restrictionType,
      value,
      currentPendingChanges: pendingChanges
    });
    
    setPendingChanges(prev => {
      const updated = {
        ...prev,
        [restrictionType]: value
      };
      console.log('Updated pendingChanges:', updated);
      return updated;
    });
  };
  const handleSave = async () => {
    console.log('Save device restrictions button clicked');
    if (Object.keys(pendingChanges).length === 0) return;
    
    setIsSaving(true);
    try {
      console.log('Pending changes to save:', pendingChanges);

      // Start with ALL existing device-level restrictions to preserve them
      const backendValues: Record<string, boolean> = {};
      
      // First, copy all existing device-level restrictions
      Object.entries(deviceRestrictions).forEach(([backendKey, sourceValue]) => {
        if (sourceValue === "device" || sourceValue === false) {
          // This is a device-level restriction, preserve it
          backendValues[backendKey] = sourceValue === "device" ? true : false;
        }
      });
      
      // Then apply the pending changes (overwrites if same key)
      Object.entries(pendingChanges).forEach(([restrictionType, value]) => {
        const restriction = restrictions.find(r => r.type === restrictionType);
        if (restriction) {
          backendValues[restriction.backend_name] = !value;
        }
      });
      
      console.log('Backend restriction values to save:', backendValues);
      await updateDeviceRestrictions({
        device: device.id as Id<"devices">,
        restrictions: backendValues
      });
      
      console.log('Device restrictions saved successfully');
      toast.success('Device restrictions updated successfully');
      setIsEditMode(false);
      setPendingChanges({});
    } catch (error) {
      console.error('Error saving device restrictions:', error);
      toast.error('Failed to save restrictions');
    } finally {
      setIsSaving(false);
    }
  };
  const handleCancel = () => {
    console.log('Cancel device restrictions button clicked');
    setIsEditMode(false);
    setPendingChanges({});
  };

  const handleRevertToDefaults = async () => {
    console.log('Revert to defaults button clicked');
    setIsSaving(true);
    try {
      // Remove all device-specific overrides by setting empty restrictions
      await updateDeviceRestrictions({
        device: device.id as Id<"devices">,
        restrictions: {}
      });
      
      console.log('Device restrictions reverted to defaults');
      toast.success('All device-specific restriction overrides have been removed. Using level defaults.');
      setIsEditMode(false);
      setPendingChanges({});
    } catch (error) {
      console.error('Error reverting to defaults:', error);
      toast.error('Failed to revert to defaults');
    } finally {
      setIsSaving(false);
    }
  };
  if (isLoadingRestrictions) {
    console.log('DeviceRestrictionsContent in loading state');
    return <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Device Restrictions</h2>
          <p className="text-muted-foreground">Loading device restrictions...</p>
        </div>
      </div>;
  }
  console.log('DeviceRestrictionsContent rendering main content');
  return <ErrorBoundary>
      <TooltipProvider>
        <div className="h-full w-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Device Restrictions</h2>
              
            </div>
            <div className="flex items-center space-x-2">
              {!isEditMode ? <Button variant="outline" size="sm" onClick={() => {
              console.log('Edit device restrictions button clicked');
              setIsEditMode(true);
            }} disabled={false} // Always allow editing regardless of device status
            className="flex items-center space-x-2">
                  <Edit3 className="w-4 h-4" />
                  <span>Edit</span>
                </Button> : <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleRevertToDefaults}
                    disabled={isSaving}
                    className="flex items-center space-x-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span>Revert to default level settings</span>
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCancel} className="flex items-center space-x-2">
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handleSave} 
                    disabled={isSaving || Object.keys(pendingChanges).length === 0} 
                    className="flex items-center space-x-2"
                  >
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </Button>
                </div>}
            </div>
          </div>


          {/* Restrictions Table */}
          <div className="flex-1 overflow-auto">
            <ErrorBoundary>
              <Card className="m-6 mb-0">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-0">Restriction</TableHead>
                        <TableHead className="w-20 sm:w-24">Status</TableHead>
                        <TableHead className="hidden sm:table-cell w-20">Source</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {flatRestrictions.map(setting => {
                      const {
                        source,
                        levelName
                      } = getRestrictionSource(setting.type);
                      const value = getRestrictionValue(setting.type);
                      const isDisabled = !isEditMode; // Always allow editing when in edit mode
                      const isChild = Boolean(setting.parent_id);
                      const currentValues = getCurrentRestrictionValues();
                      const childDisabled = shouldChildBeDisabled(setting, currentValues);
                      const childHidden = shouldChildBeHidden(setting, currentValues);
                      
                      // Hide Siri child restrictions when Siri is restricted
                      if (childHidden) {
                        return null;
                      }
                      
                      console.log('Rendering restriction row:', {
                        type: setting.type,
                        isEditMode,
                        value,
                        childDisabled,
                        pendingChanges: pendingChanges[setting.type]
                      });
                      return <TableRow key={setting.type} className={isChild ? "bg-muted/30" : ""}>
                            <TableCell className="min-w-0">
                              <div className="flex items-center space-x-2">
                                <span className={`font-medium text-sm ${isChild ? 'ml-6 text-muted-foreground' : ''} truncate`}>
                                  {isChild && '└─ '}{setting.name}
                                  {setting.description && (
                                    <span className="text-muted-foreground font-normal"> ({setting.description})</span>
                                  )}
                                </span>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Backend key: {setting.backend_name}</p>
                                    {isChild && <p className="text-xs text-muted-foreground mt-1">
                                        Child restriction - automatically restricted when parent is restricted
                                      </p>}
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                              <div className="text-xs text-gray-500 sm:hidden">
                                {getSourceDisplay(source, levelName)} • {value ? 'Allowed' : 'Restricted'}
                              </div>
                            </TableCell>
                            <TableCell className="w-20 sm:w-24">
                              {(() => {
                                console.log('Rendering switch cell for:', setting.type, 'isEditMode:', isEditMode);
                                return isEditMode ? (
                                  <Switch 
                                    checked={value} 
                                    onCheckedChange={checked => {
                                      console.log('Device restriction switch changed:', {
                                        type: setting.type,
                                        checked
                                      });
                                      handleEditRestriction(setting.type, checked);
                                    }} 
                                    disabled={childDisabled} 
                                    className={`
                                      ${value ? 'data-[state=checked]:bg-green-600' : 'data-[state=unchecked]:bg-red-500'}
                                    `} 
                                  />
                                ) : (
                                  <Badge 
                                    variant={value ? "default" : "destructive"}
                                    className="text-xs"
                                  >
                                    {value ? "Allowed" : "Restricted"}
                                  </Badge>
                                );
                              })()}
                              {childDisabled && <Badge variant="secondary" className="text-xs ml-2">
                                  Auto-restricted
                                </Badge>}
                            </TableCell>
                            <TableCell className="hidden sm:table-cell w-20">
                              <Badge variant="outline" className="text-xs">
                                {getSourceDisplay(source, levelName)}
                              </Badge>
                            </TableCell>
                          </TableRow>;
                    })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </ErrorBoundary>

            {/* Info Card */}
            <Card className="border-blue-200 bg-blue-50 m-6">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">Device Restriction Management</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Device-specific restrictions override level settings. Changes are applied immediately to the device configuration.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </TooltipProvider>
    </ErrorBoundary>;
};